import React, { createContext, useContext, useState, useRef, useEffect } from 'react';
import API_BASE_URL, { looksLikeJwt, parseJwtPayload } from './config';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authBootstrapped, setAuthBootstrapped] = useState(false);
  const [sessionTimeoutMinutes, setSessionTimeoutMinutes] = useState(30); // Default 30 minutes
  const [isAdminContext, setIsAdminContext] = useState(false); // Track if we're in admin context
  const timeoutRef = useRef(null);
  const lastActivityRef = useRef(Date.now());

  // Session timeout disabled - clear any existing timers and do nothing
  const resetSessionTimeout = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    return; // no-op: session timeout logic removed
  };

  // Auto-logout disabled - no alerts, no forced logout
  const handleAutoLogout = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    return; // no-op: session timeout logic removed
  };

  const updateSessionTimeout = (minutes) => {
    setSessionTimeoutMinutes(minutes);
    localStorage.setItem('sessionTimeoutMinutes', minutes.toString());
    
    // Only reset timeout if not in admin context
    if (isAuthenticated && !isAdminContext) {
      resetSessionTimeout();
    }
  };

  // Activity tracking
  const trackActivity = () => {
    lastActivityRef.current = Date.now();
    // Only reset timeout if not in admin context
    if (isAuthenticated && !isAdminContext) {
      resetSessionTimeout();
    }
  };

  // Set admin context (disable session timeout)
  const setAdminContext = (isAdmin) => {
    setIsAdminContext(isAdmin);
    
    // Clear any existing timeout when entering admin context
    if (isAdmin && timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  };

  const login = (token, userData = null) => {
    if (!looksLikeJwt(token)) {
      console.error('[AUTH] Login response did not include a valid JWT');
      return;
    }
    localStorage.setItem('authToken', token.trim());

    try {
      const payload = parseJwtPayload(token) || {};
      const userInfo = {
        id: payload.user_id ?? userData?.id,
        username: userData?.username || 'Admin',
        email: userData?.email ?? payload.email,
        role: userData?.role || payload.role || 'org_admin',
        organization_id: userData?.org_id ?? payload.org_id ?? payload.organization_id,
        organization_name: userData?.organization_name,
      };

      setUser(userInfo);
      setIsAuthenticated(true);
      setAuthBootstrapped(true);
      localStorage.setItem('adminUser', JSON.stringify(userInfo));
      localStorage.setItem('isAuthenticated', 'true');
      lastActivityRef.current = Date.now();
      if (!isAdminContext) {
        resetSessionTimeout();
      }
    } catch (error) {
      console.error('Error decoding JWT token:', error);
      const userInfo = {
        username: 'Admin',
        ...(userData || {}),
        role: userData?.role || 'org_admin',
      };
      setUser(userInfo);
      setIsAuthenticated(true);
      setAuthBootstrapped(true);
      localStorage.setItem('adminUser', JSON.stringify(userInfo));
      localStorage.setItem('isAuthenticated', 'true');
    }
  };

  const logout = () => {
    setUser(null);
    setIsAuthenticated(false);
    setIsAdminContext(false);
    // Clear HttpOnly JWT cookie set by backend (ignored if unreachable)
    try {
      fetch(`${API_BASE_URL}/api/auth/logout`, {
        method: 'POST',
        credentials: 'include',
      }).catch(() => {});
    } catch (_) {
      /* ignore */
    }
    // Clear localStorage
    localStorage.removeItem('adminUser');
    localStorage.removeItem('isAuthenticated');
    localStorage.removeItem('authToken');
    
    // Clear session timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  };
  
  // Get auth token for API requests
  const getAuthToken = () => {
    return localStorage.getItem('authToken');
  };

  // Check for existing session on app load
  useEffect(() => {
    const savedUser = localStorage.getItem('adminUser');
    const savedAuth = localStorage.getItem('isAuthenticated');
    const savedToken = localStorage.getItem('authToken');
    const savedTimeout = localStorage.getItem('sessionTimeoutMinutes');
    
    // Load saved session timeout setting
    if (savedTimeout) {
      setSessionTimeoutMinutes(parseInt(savedTimeout));
    }
    
    // Check if we have both token and user data
    if (savedToken && savedUser && savedAuth === 'true') {
      try {
        const userData = JSON.parse(savedUser);
        
        // Verify token is still valid (check expiration)
        try {
          const payload = parseJwtPayload(savedToken);
          const exp = payload?.exp ? payload.exp * 1000 : 0;
          if (exp && Date.now() > exp) {
            localStorage.removeItem('adminUser');
            localStorage.removeItem('isAuthenticated');
            localStorage.removeItem('authToken');
            return;
          }
        } catch (e) {
          // Token decode failed, clear auth
          localStorage.removeItem('adminUser');
          localStorage.removeItem('isAuthenticated');
          localStorage.removeItem('authToken');
          return;
        }
        
        setUser(userData);
        setIsAuthenticated(true);
        lastActivityRef.current = Date.now();
        resetSessionTimeout();
      } catch (error) {
        // If parsing fails, clear the data
        localStorage.removeItem('adminUser');
        localStorage.removeItem('isAuthenticated');
        localStorage.removeItem('authToken');
      }
    } else if (savedUser || savedAuth) {
      // Clean up if token is missing
      localStorage.removeItem('adminUser');
      localStorage.removeItem('isAuthenticated');
      localStorage.removeItem('authToken');
    }
    setAuthBootstrapped(true);
  }, []);

  // Activity tracking effect
  useEffect(() => {
    if (!isAuthenticated || isAdminContext) return;

    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
    
    const handleActivity = () => {
      trackActivity();
    };

    // Add event listeners
    events.forEach(event => {
      document.addEventListener(event, handleActivity, true);
    });

    // Cleanup
    return () => {
      events.forEach(event => {
        document.removeEventListener(event, handleActivity, true);
      });
    };
  }, [isAuthenticated, isAdminContext]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const value = {
    user,
    isAuthenticated,
    authBootstrapped,
    login,
    logout,
    getAuthToken,
    sessionTimeoutMinutes,
    updateSessionTimeout,
    trackActivity,
    setAdminContext,
    isAdminContext
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
